import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { myFetch } from '@commons/fetch';
import { applySideEffects } from './foldersSlice';

// Types
interface FilterPageState {
  items: any[];
  models: any[];
  isLoading: boolean;
  error: string | null;
  // Filter-specific filter states (stored per filterId)
  filterFilters: {
    [filterId: string]: {
      search: any;
      currentPage: number;
      itemsPerPage: number;
    };
  };
}

const initialState: FilterPageState = {
  items: [],
  models: [],
  isLoading: false,
  error: null,
  filterFilters: {},
};

// Async Thunks
export const fetchItems = createAsyncThunk(
  'filterPage/fetchItems',
  async ({
    username,
    category,
    type,
  }: {
    username: string;
    category: string;
    type: string;
  }) => {
    const response = await myFetch(
      `/${username}/files?category=${category}&type=${type}`
    );
    return response.data?.files || [];
  }
);

export const fetchModels = createAsyncThunk(
  'filterPage/fetchModels',
  async ({ username, category }: { username: string; category: string }) => {
    const response = await myFetch(
      `/${username}/models?category=${category}&type=ANNEX`
    );
    return response.data || [];
  }
);

export const updateFile = createAsyncThunk(
  'filterPage/updateFile',
  async ({
    folderName,
    fileName,
    updates,
  }: {
    folderName: string;
    fileName: string;
    updates: any;
  }) => {
    await myFetch(`/folders/${folderName}/files/${fileName}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    // Return the updates to apply them to Redux state
    return { folderName, fileName, updates };
  }
);

export const performItemAction = createAsyncThunk(
  'filterPage/performItemAction',
  async ({
    folderName,
    fileName,
    action,
    data,
  }: {
    folderName: string;
    fileName: string;
    action: string;
    data?: any;
  }) => {
    await myFetch(`/folders/${folderName}/files/${fileName}/actions`, {
      method: 'PUT',
      body: JSON.stringify({ action, data }),
    });
    // Return the action to apply it to Redux state
    return { folderName, fileName, action, data };
  }
);

export const deleteItem = createAsyncThunk(
  'filterPage/deleteItem',
  async ({
    folderName,
    fileName,
  }: {
    folderName: string;
    fileName: string;
  }) => {
    await myFetch(`/folders/${folderName}/files/${fileName}`, {
      method: 'PUT',
      body: JSON.stringify({ action: 'delete' }),
    });
    return { folderName, fileName };
  }
);

export const sendEmail = createAsyncThunk(
  'filterPage/sendEmail',
  async ({
    username,
    folderName,
    fileName,
    to,
    subject,
    message,
  }: {
    username: string;
    folderName: string;
    fileName: string;
    to: string;
    subject: string;
    message: string;
  }) => {
    const response = await myFetch(
      `/${username}/folders/${folderName}/files/${fileName}/send-email`,
      {
        method: 'POST',
        body: JSON.stringify({ to, subject, message }),
      }
    );
    return response.data;
  }
);

export const addAnnex = createAsyncThunk(
  'filterPage/addAnnex',
  async ({
    username,
    folderName,
    model,
  }: {
    username: string;
    folderName: string;
    model: any;
  }) => {
    const newFile = {
      name: model.name,
      type: model.type,
      category: model.category,
      template: model.template,
      fields: model.fields,
    };
    const response = await myFetch(`/${username}/folders/${folderName}/files`, {
      method: 'POST',
      body: JSON.stringify(newFile),
    });
    return { folderName, annex: response.data };
  }
);

export const deleteAnnex = createAsyncThunk(
  'filterPage/deleteAnnex',
  async ({
    username,
    folderName,
    fileName,
  }: {
    username: string;
    folderName: string;
    fileName: string;
  }) => {
    await myFetch(`/${username}/folders/${folderName}/files/${fileName}`, {
      method: 'DELETE',
    });
    return { folderName, fileName };
  }
);

export const duplicateAnnex = createAsyncThunk(
  'filterPage/duplicateAnnex',
  async ({
    username,
    folderName,
    fileName,
  }: {
    username: string;
    folderName: string;
    fileName: string;
  }) => {
    const response = await myFetch(
      `/${username}/folders/${folderName}/files/${fileName}/duplicate`,
      {
        method: 'POST',
      }
    );
    return { folderName, annex: response.data };
  }
);

export const importFields = createAsyncThunk(
  'filterPage/importFields',
  async ({
    username,
    folderName,
    fileName,
    fields,
  }: {
    username: string;
    folderName: string;
    fileName: string;
    fields: any;
  }) => {
    const response = await myFetch(
      `/${username}/folders/${folderName}/files/${fileName}/import-fields`,
      {
        method: 'POST',
        body: JSON.stringify({ fields }),
      }
    );
    return { folderName, fileName, fields: response.data };
  }
);

const filterPageSlice = createSlice({
  name: 'filterPage',
  initialState,
  reducers: {
    // Update an item optimistically
    updateItem: (state, action: PayloadAction<any>) => {
      const updatedItem = action.payload;
      state.items = state.items.map((item) =>
        item.folderName === updatedItem.folderName &&
        item.fileName === updatedItem.fileName
          ? { ...item, ...updatedItem, updatedAt: new Date().toISOString() }
          : item
      );
    },
    // Update annex within an item
    updateAnnex: (
      state,
      action: PayloadAction<{ folderName: string; annex: any }>
    ) => {
      const { folderName, annex } = action.payload;
      state.items = state.items.map((item) => {
        if (item.folderName === folderName) {
          return {
            ...item,
            annexes: (item.annexes || []).map((a: any) =>
              a.fileName === annex.fileName
                ? { ...a, ...annex, updatedAt: new Date().toISOString() }
                : a
            ),
          };
        }
        return item;
      });
    },
    // Set all items (for backward compatibility)
    setItems: (state, action: PayloadAction<any[]>) => {
      state.items = action.payload;
    },
    // Clear items
    clearItems: (state) => {
      state.items = [];
    },
    // Set filter filters
    setFilterFilters: (
      state,
      action: PayloadAction<{
        filterId: string;
        search?: any;
        currentPage?: number;
        itemsPerPage?: number;
      }>
    ) => {
      const { filterId, search, currentPage, itemsPerPage } = action.payload;
      if (!state.filterFilters[filterId]) {
        state.filterFilters[filterId] = {
          search: {},
          currentPage: 1,
          itemsPerPage: 10,
        };
      }
      if (search !== undefined) state.filterFilters[filterId].search = search;
      if (currentPage !== undefined)
        state.filterFilters[filterId].currentPage = currentPage;
      if (itemsPerPage !== undefined)
        state.filterFilters[filterId].itemsPerPage = itemsPerPage;
    },
    // Clear error
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Items
    builder
      .addCase(fetchItems.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchItems.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = action.payload;
      })
      .addCase(fetchItems.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch items';
      });

    // Fetch Models
    builder
      .addCase(fetchModels.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchModels.fulfilled, (state, action) => {
        state.models = action.payload;
      })
      .addCase(fetchModels.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to fetch models';
      });

    // Update File
    builder
      .addCase(updateFile.fulfilled, (state, action) => {
        const { folderName, fileName, updates } = action.payload;

        // Helper function to apply side effects
        const applyUpdatesWithSideEffects = (fileObj: any) => {
          const updatedFile = { ...fileObj, ...updates };

          // Update the timestamp for every update
          updatedFile.updatedAt = new Date().toISOString();

          // Check if fields are being updated
          const hasFieldsUpdate =
            updates.fields !== undefined &&
            updates.fields !== null &&
            Array.isArray(updates.fields);

          // Apply side effects using shared function
          applySideEffects(updatedFile, updates, hasFieldsUpdate);

          return updatedFile;
        };

        state.items = state.items.map((item) => {
          // Check if this is the main item being updated
          if (item.folderName === folderName && item.fileName === fileName) {
            return applyUpdatesWithSideEffects(item);
          }

          // Check if any annex in this item matches
          if (item.annexes && item.annexes.length > 0) {
            const updatedAnnexes = item.annexes.map((annex: any) =>
              annex.fileName === fileName
                ? applyUpdatesWithSideEffects(annex)
                : annex
            );

            // Only return updated item if an annex was actually updated
            if (
              updatedAnnexes.some((a: any, i: number) => a !== item.annexes[i])
            ) {
              return { ...item, annexes: updatedAnnexes };
            }
          }

          return item;
        });
      })
      .addCase(updateFile.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update file';
      });

    // Perform Item Action
    builder
      .addCase(performItemAction.fulfilled, (state, action) => {
        const {
          folderName,
          fileName,
          action: actionName,
          data,
        } = action.payload;

        // Helper function to apply action effects
        const applyActionEffects = (fileObj: any) => {
          const updates: any = { ...fileObj };

          // Update the timestamp for every action
          updates.updatedAt = new Date().toISOString();

          // Handle specific actions
          // Note: lock/unlock, publish/unpublish, verify, sign, and archive now use updateFile with boolean properties
          switch (actionName) {
            case 'rename':
              if (data?.name) updates.name = data.name;
              break;
            case 'moveBackOfficeStatusToInProgress':
              updates.backOfficeStatus = 3;
              break;
            case 'moveBackOfficeStatusToFinalized':
              updates.backOfficeStatus = 4;
              break;
            case 'moveBackOfficeStatusToNull':
              updates.backOfficeStatus = null;
              break;
            default:
              break;
          }

          return updates;
        };

        // Apply action effects to state
        state.items = state.items.map((item) => {
          // Check if this is the main item being updated
          if (item.folderName === folderName && item.fileName === fileName) {
            return applyActionEffects(item);
          }

          // Check if any annex in this item matches
          if (item.annexes && item.annexes.length > 0) {
            const updatedAnnexes = item.annexes.map((annex: any) =>
              annex.fileName === fileName ? applyActionEffects(annex) : annex
            );

            // Only return updated item if an annex was actually updated
            if (
              updatedAnnexes.some((a: any, i: number) => a !== item.annexes[i])
            ) {
              return { ...item, annexes: updatedAnnexes };
            }
          }

          return item;
        });
      })
      .addCase(performItemAction.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to perform action';
      });

    // Delete Item
    builder.addCase(deleteItem.fulfilled, (state, action) => {
      const { folderName, fileName } = action.payload;
      state.items = state.items.filter(
        (item) =>
          !(item.folderName === folderName && item.fileName === fileName)
      );
    });

    // Send Email
    builder.addCase(sendEmail.fulfilled, (state, action) => {
      const updatedItem = action.payload;
      state.items = state.items.map((item) =>
        item.folderName === updatedItem.folderName &&
        item.fileName === updatedItem.fileName
          ? { ...item, ...updatedItem }
          : item
      );
    });

    // Add Annex
    builder.addCase(addAnnex.fulfilled, (state, action) => {
      const { folderName, annex } = action.payload;
      state.items = state.items.map((item) =>
        item.folderName === folderName
          ? {
              ...item,
              annexes: [...(item.annexes || []), annex],
            }
          : item
      );
    });

    // Delete Annex
    builder.addCase(deleteAnnex.fulfilled, (state, action) => {
      const { folderName, fileName } = action.payload;
      state.items = state.items.map((item) =>
        item.folderName === folderName
          ? {
              ...item,
              annexes: (item.annexes || []).filter(
                (a: any) => a.fileName !== fileName
              ),
            }
          : item
      );
    });

    // Duplicate Annex
    builder.addCase(duplicateAnnex.fulfilled, (state, action) => {
      const { folderName, annex } = action.payload;
      state.items = state.items.map((item) =>
        item.folderName === folderName
          ? {
              ...item,
              annexes: [...(item.annexes || []), annex],
            }
          : item
      );
    });

    // Import Fields
    builder.addCase(importFields.fulfilled, (state, action) => {
      const { folderName, fileName, fields } = action.payload;
      state.items = state.items.map((item) => {
        if (item.folderName === folderName) {
          return {
            ...item,
            annexes: (item.annexes || []).map((a: any) =>
              a.fileName === fileName
                ? {
                    ...a,
                    fields: fields,
                    updatedAt: new Date().toISOString(),
                  }
                : a
            ),
          };
        }
        return item;
      });
    });
  },
});

export const {
  updateItem,
  updateAnnex,
  setItems,
  clearItems,
  setFilterFilters,
  clearError,
} = filterPageSlice.actions;

export default filterPageSlice.reducer;
